import React, { useState } from 'react';
import type { MutableRefObject } from 'react';
import { omit } from 'lodash-es';

import { useLocal } from '@/pages/dashboard/utils/customHook';
import ResizeAwareBarChart from './ResizeAwareBarChart';
import ResizeAwareLineChart from './ResizeAwareLineChart';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import CardSlot from '@/pages/dashboard/components/CardSlot';
import {
  formatDataByChartType,
  xAxisHighLight,
  xAxisLabelFormat,
} from '@/pages/dashboard/components/Chart/_config/plug';
import {
  formatNum,
  formatPercentNum,
  getChartDateFormatString,
  handleDownload,
  unitConversion,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import moment from 'moment';


const SwitchChart = (props: any) => {
  const {
    title,
    loading,
    isError,
    isNoData,
    timeType,
    slot,
    /** 与同行右侧 Segment 区联动：左侧无 tab 时把该高度加到图表高度上（由 TrendPairRow 测量），避免标题下留白 */
    syncedToolbarHeight,
    /** 右侧有 tab 时挂到 Segment 外层，供 ResizeObserver 测量 */
    slotStripRef,
    isPercent = false,
    isInt = false,
    filterSelector,
    chartHeight: chartHeightProps,
    chartOptions,
    noDataText,
    tableOptions: tableOptionsProps,
    isLaunchTime,
    isEarliestData,
  } = props;
  const { getLocalText } = useLocal();
  const [chartType, setChartType] = useState('discount-chart');

  const barOptions = formatDataByChartType('bar', chartOptions);
  const lineOptions = formatDataByChartType('line', chartOptions);
  const tableOptions = formatDataByChartType('table', tableOptionsProps);
  const baseChartHeight = chartHeightProps ?? 340;
  /** 左侧无 Segment 时：图表高度 = 基准高度 + 右侧 Segment 区高度，与右侧「tab+图表」总可视高度对齐且不占空白条 */
  const chartHeight =
    typeof syncedToolbarHeight === 'number' && !slot
      ? baseChartHeight + syncedToolbarHeight
      : baseChartHeight;

  function formatterFunc(list: any) {
    const temp = {
      title: list[0].name,
      data: [] as Array<{}>,
    };
    const sortList =
      list
        ?.slice(1, list.length)
        ?.sort((a: any, b: any) => b.value - a.value) || [];
    list[0] && sortList.unshift(list[0]);
    sortList.forEach((ele: any) => {
      temp.data.push({
        name: ele.seriesName,
        metricsNum: isPercent
          ? formatPercentNum(ele.value)
          : formatNum(ele.value, false, isInt),
        color: ele.color,
      });
    });
    return getEchartTooltipText(temp);
  }

  const axisLabelFormat = (opt: any, timeType: any) => {
    return opt.xAxis?.map((item: any) => {
      return {
        ...item,
        axisLabel: {
          ...item.axisLabel,
          formatter: (value: any) => getChartDateFormatString(timeType, value),
        },
      };
    });
  };

  const setOtherConfigFunc = (opt: any, timeType: any) => {
    opt.xAxis = axisLabelFormat(opt, timeType);
    const yAxisLabelFormat = (value: any) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      const num =
        value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
      return num;
    };
    opt.yAxis.axisLabel.formatter = (value: any) => {
      if (!isPercent) return yAxisLabelFormat(value);
      if (value > 0 && value < 0.1 && String(value).length > 4)
        return formatPercentNum(+value, 1);
      return formatPercentNum(value, 0);
    };

    const legendRich: any = {};
    const legendMap: any = {};
    const xArr = Array(opt?.legend?.data?.length + 11)
      .fill(0)
      .map((_, i) => `icon${i}`);
    filterSelector?.competitor_list?.forEach((item: any, index: number) => {
      legendMap[item?.name?.replace(/\s*/g, '')] = xArr[index];
      legendRich[xArr[index]] = {
        fontSize: 16,
        width: 16,
        height: 16,
        backgroundColor: {
          image: item?.cover_url,
        },
      };
    });
    const newLegendRich = omit(legendRich, ['undefined']);

    opt.legend = {
      ...opt.legend,
      data: opt.legend.data?.map((legendItem: any) => {
        return {
          name: legendItem,
          icon: 'circle',
        };
      }),
      formatter: (name: any) => {
        const richName = legendMap[name?.replace(/\s*/g, '')];
        if (richName) {
          return `{${richName}| } ${name}`;
        }
        return name;
      },
      textStyle: {
        color: '#838967',
        rich: newLegendRich,
      },
    };
    // y2：绘图区（含 x 轴刻度）底部到容器底部的距离，需盖住底部 scroll 图例+翻页。
    // 过大会在「趋势线与图例」之间留出明显空白；与 makeupOptions 默认 30 对齐略增即可。
    opt.grid = {
      ...opt.grid,
      y2: 32,
      containLabel: true,
    };
    return opt;
  };

  const handleAxisFormat = (value: any) => {
    if (isLaunchTime || isEarliestData) {
      return value;
    }
    return timeType === 'Month'
      ? moment(value).format('YYYY-MM')
      : moment(value).format('MM-DD');
  };

  const chartMap: any = {
    'column-chart': {
      content: getLocalText('common_bar_chart'),
      component: (
        <ResizeAwareBarChart
          height={chartHeight || 340}
          legendData={barOptions?.legend ?? []}
          xAxisData={barOptions?.xAxisData ?? []}
          series={barOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => {
                return handleAxisFormat(params.value);
              },
            });
            return setOtherConfigFunc(opt, timeType);
          }}
        />
      ),
    },
    'discount-chart': {
      content: getLocalText('common_line_chart'),
      component: (
        <ResizeAwareLineChart
          height={chartHeight || 340}
          legendData={lineOptions?.legend ?? []}
          xAxisData={lineOptions?.xAxisData ?? []}
          series={lineOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = setOtherConfigFunc(opt, timeType);
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => {
                return handleAxisFormat(params.value);
              },
            });
            return opt;
          }}
        />
      ),
    },
    sheet: {
      content: getLocalText('common_table'),
      component: (
        <MiniTable
          loading={loading}
          height={chartHeight || 340}
          tableOption={tableOptions?.option}
          tableList={tableOptions?.data}
          recordKey={'date'}
          className="dbd-competitor-mini-table"
        />
      ),
    },
  };

  const download = () => {
    const name = (tableOptions.downloadTitle ?? title)?.replace(/\u0020/g, '_');
    handleDownload(tableOptions, name);
  };

  const assignSlotStripRef = (el: HTMLDivElement | null) => {
    if (slotStripRef) {
      (slotStripRef as MutableRefObject<HTMLDivElement | null>).current = el;
    }
  };

  const showSlotStrip = Boolean(slot);

  return (
    <CardSlot
      getChange={setChartType}
      title={title}
      iconList={[
        { icon: 'column-chart', context: getLocalText('common_bar_chart') },
        { icon: 'discount-chart', context: getLocalText('common_line_chart') },
        { icon: 'sheet', context: getLocalText('common_table') },
      ]}
      iconShow={isNoData}
      isEyes={true}
      activeTab={chartType}
      onDownload={download}
    >
      {showSlotStrip ? (
        <div ref={assignSlotStripRef} className="dbd-key-metrics-trend-slot">
          {slot}
        </div>
      ) : null}
      <Abnormal
        nodataMsg={noDataText}
        style={{ height: `${chartHeight || 340}px` }}
        component={chartMap?.[chartType]?.component}
        isLoading={loading}
        isError={isError}
        isNoData={isNoData}
      />
    </CardSlot>
  );
};

export default SwitchChart;
